import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { 
  FaTimes, 
  FaCode, 
  FaInfoCircle, 
  FaExclamationTriangle, 
  FaSave, 
  FaCloudDownloadAlt, 
  FaChevronRight, 
  FaTerminal, 
  FaFileCode
} from 'react-icons/fa';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'cloud-init-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'cloud-init-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'cloud-init-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Error message constants
const ERROR_MESSAGES = {
  'SCRIPT_EMPTY': 'Script cannot be empty',
  'BASH_HEADER_MISSING': 'Bash scripts must start with #!/bin/bash',
  'CLOUD_CONFIG_HEADER_MISSING': 'Cloud-config scripts must start with #cloud-config',
  'SAVE_FAILED': 'Failed to save script. Please try again.',
  'LOAD_EXAMPLE_FAILED': 'Failed to load example template',
  'SCRIPT_SIZE_EXCEEDED': 'Script exceeds maximum size of 16KB. It will be truncated.'
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Sanitize script content (preserve script syntax but limit size)
const sanitizeScript = (text) => {
  if (!text || typeof text !== 'string') return '';
  // Don't replace script-critical characters, but limit length
  return text.substring(0, 16384); // 16KB limit
};

const CloudInitModal = ({ isOpen, onClose, onSave, initialScript = '' }) => {
    const [script, setScript] = useState(initialScript);
    const [scriptType, setScriptType] = useState('bash');
    const [validationError, setValidationError] = useState('');
    const [error, setError] = useState('');
    const textareaRef = useRef(null);
    
    // Set Sentry context when modal state changes
    useEffect(() => {
      if (isOpen) {
        Sentry.setContext("cloud-init-modal", {
          isOpen,
          scriptType,
          scriptLength: script.length,
          hasValidationError: !!validationError,
          hasError: !!error,
          hasInitialScript: !!initialScript
        });
      }
    }, [isOpen, scriptType, script.length, validationError, error, initialScript]);
    
    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            const startTime = performance.now();
            
            logInfo('Cloud-init modal opened', {
                hasInitialScript: !!initialScript,
                initialScriptLength: initialScript.length,
                initialScriptPreview: initialScript.substring(0, 100)
            });
            
            logUserAction('cloud_init_modal_opened', {
                hasInitialScript: !!initialScript,
                initialScriptLength: initialScript.length
            });
            
            // Detect script type from existing content
            const sanitizedInitialScript = sanitizeScript(initialScript);
            let detectedType = 'bash';
            let finalScript = '';
            
            if (sanitizedInitialScript.trim().startsWith('#cloud-config')) {
                detectedType = 'cloud-config';
                finalScript = sanitizedInitialScript;
                
                logInfo('Script type detected as cloud-config', {
                    scriptLength: sanitizedInitialScript.length
                });
            } else if (sanitizedInitialScript.trim().startsWith('#!/bin/bash')) {
                detectedType = 'bash';
                finalScript = sanitizedInitialScript;
                
                logInfo('Script type detected as bash', {
                    scriptLength: sanitizedInitialScript.length
                });
            } else {
                // Default to bash if no script exists or doesn't match patterns
                detectedType = 'bash';
                finalScript = sanitizedInitialScript || '#!/bin/bash\n# Add your initialization commands here\n';
                
                logInfo('Script type defaulted to bash', {
                    hadContent: !!sanitizedInitialScript,
                    contentLength: sanitizedInitialScript.length,
                    usedTemplate: !sanitizedInitialScript
                });
            }
            
            setScriptType(detectedType);
            setScript(finalScript);
            setValidationError('');
            setError('');
            
            // Focus textarea when modal opens
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    
                    logInfo('Textarea focused', {
                        scriptType: detectedType,
                        scriptLength: finalScript.length
                    });
                }
                
                const endTime = performance.now();
                const duration = endTime - startTime;
                
                logPerformance('modal_initialization', duration, {
                    detectedType,
                    scriptLength: finalScript.length,
                    hadInitialScript: !!initialScript
                });
            }, 100);
        }
    }, [isOpen, initialScript]);
    
    // Validate script based on selected type
    const validateScript = () => {
        const startTime = performance.now();
        const trimmedScript = script.trim();
        
        logInfo('Starting script validation', {
            scriptType,
            scriptLength: script.length,
            trimmedLength: trimmedScript.length
        });
        
        if (trimmedScript.length === 0) {
            setValidationError(ERROR_MESSAGES['SCRIPT_EMPTY']);
            
            logError('Script validation failed - empty script', {
                scriptType,
                originalLength: script.length
            });
            
            return false;
        }
        
        if (scriptType === 'bash' && !trimmedScript.startsWith('#!/bin/bash')) {
            setValidationError(ERROR_MESSAGES['BASH_HEADER_MISSING']);
            
            logError('Script validation failed - invalid bash header', {
                scriptType,
                scriptStart: trimmedScript.substring(0, 50),
                expectedStart: '#!/bin/bash'
            });
            
            return false;
        }
        
        if (scriptType === 'cloud-config' && !trimmedScript.startsWith('#cloud-config')) {
            setValidationError(ERROR_MESSAGES['CLOUD_CONFIG_HEADER_MISSING']);
            
            logError('Script validation failed - invalid cloud-config header', {
                scriptType,
                scriptStart: trimmedScript.substring(0, 50),
                expectedStart: '#cloud-config'
            });
            
            return false;
        }
        
        setValidationError('');
        
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('script_validation', duration, {
            scriptType,
            scriptLength: trimmedScript.length,
            success: true
        });
        
        logInfo('Script validation passed', {
            scriptType,
            scriptLength: trimmedScript.length,
            duration: `${duration.toFixed(2)}ms`
        });
        
        return true;
    };
    
    // Handle script type change
    const handleTypeChange = (newType) => {
        const previousType = scriptType;
        const previousScript = script;
        
        logUserAction('script_type_changed', {
            previousType,
            newType,
            scriptLength: script.length,
            willUseTemplate: (newType === 'bash' && !script.includes('#!/bin/bash')) || 
                           (newType === 'cloud-config' && !script.includes('#cloud-config'))
        });
        
        setScriptType(newType);
        
        // Update script template when type changes
        if (newType === 'bash' && !script.includes('#!/bin/bash')) {
            const newScript = '#!/bin/bash\n# Add your initialization commands here\n';
            setScript(newScript);
            
            logInfo('Applied bash template', {
                previousType,
                newType,
                previousScriptLength: script.length,
                newScriptLength: newScript.length
            });
        } else if (newType === 'cloud-config' && !script.includes('#cloud-config')) {
            const newScript = '#cloud-config\n\n# Add your cloud-config directives here\n';
            setScript(newScript);
            
            logInfo('Applied cloud-config template', {
                previousType,
                newType,
                previousScriptLength: script.length,
                newScriptLength: newScript.length
            });
        }
        
        // Clear any previous validation errors when changing type
        setValidationError('');
        
        logInfo('Script type change completed', {
            from: previousType,
            to: newType,
            clearedValidationError: !!validationError
        });
    };
    
    // Handle save with validation and sanitization
    const handleSave = () => {
        const startTime = performance.now();
        
        logUserAction('save_script_initiated', {
            scriptType,
            scriptLength: script.length,
            hasValidationError: !!validationError
        });
        
        logInfo('Starting script save process', {
            scriptType,
            scriptLength: script.length,
            preview: script.substring(0, 100)
        });
        
        if (validateScript()) {
            try {
                // Size limit check BEFORE sanitization
                if (script.length > 16384) {
                    setError(ERROR_MESSAGES['SCRIPT_SIZE_EXCEEDED']);
                    
                    logError('Script exceeds size limit', {
                        scriptType,
                        originalLength: script.length,
                        maxLength: 16384,
                        excessCharacters: script.length - 16384
                    });
                }
                
                // Security: Sanitize script content (already limits to 16KB)
                const sanitizedScript = sanitizeScript(script);
                
                onSave(sanitizedScript);
                
                const endTime = performance.now();
                const duration = endTime - startTime;
                
                logPerformance('save_script', duration, {
                    scriptType,
                    originalLength: script.length,
                    finalLength: sanitizedScript.length,
                    wasTruncated: script.length > 16384,
                    success: true
                });
                
                logInfo('Script saved successfully', {
                    scriptType,
                    finalLength: sanitizedScript.length,
                    duration: `${duration.toFixed(2)}ms`,
                    wasTruncated: script.length > 16384
                });
                
                logUserAction('save_script_success', {
                    scriptType,
                    finalLength: sanitizedScript.length,
                    wasTruncated: script.length > 16384
                });
                
        } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    setError(ERROR_MESSAGES['SAVE_FAILED']);
    
    logError('Error saving cloud-init script', {
        scriptType,
        scriptLength: script.length,
        duration: `${duration.toFixed(2)}ms`,
        errorMessage: error.message,
        errorName: error.name
    }, error);
    
    logUserAction('save_script_error', {
        scriptType,
        scriptLength: script.length,
        errorType: error.name
    });
}
        } else {
            logUserAction('save_script_failed_validation', {
                scriptType,
                scriptLength: script.length,
                validationError
            });
        }
    };
    
    // Examples for different script types
    const examples = {
        bash: `#!/bin/bash
# Install additional packages
apt-get update
apt-get install -y curl wget htop

# Create a new user
useradd -m -s /bin/bash newuser
echo "newuser:password" | chpasswd

# Configure system settings
echo "vm.swappiness=10" >> /etc/sysctl.conf
`,
        'cloud-config': `#cloud-config

# Define user configurations
users:
  # Create a new user
  - name: newuser
    # Grant sudo privileges without a password
    sudo: ["ALL=(ALL) NOPASSWD:ALL"]
    # Add user to groups
    groups: sudo
    # Set default shell
    shell: /bin/bash

# Install packages
packages:
  - curl
  - wget
  - htop

# Write configuration files
write_files:
  - path: /etc/ssh/sshd_config
    content: |
      Port 22
      PermitRootLogin no
      PasswordAuthentication no
`
    };
    
    // Load example template
    const loadExample = () => {
        logUserAction('load_example_initiated', {
            scriptType,
            currentScriptLength: script.length,
            hasCurrentContent: script.trim().length > 0
        });
        
        try {
            const exampleScript = examples[scriptType];
            setScript(exampleScript);
            
            logInfo('Example template loaded', {
                scriptType,
                exampleLength: exampleScript.length,
                previousLength: script.length
            });
            
            logUserAction('load_example_success', {
                scriptType,
                exampleLength: exampleScript.length,
                replacedContent: script.trim().length > 0
            });
        } catch (error) {
            setError(ERROR_MESSAGES['LOAD_EXAMPLE_FAILED']);
            
            logError('Error loading example template', {
                scriptType,
                errorMessage: error.message,
                errorName: error.name
            }, error);
            
            logUserAction('load_example_error', {
                scriptType,
                errorType: error.name
            });
        }
    };
    
    // Handle keyboard shortcuts
    const handleKeyDown = (e) => {
        // Tab key handling for indentation
        if (e.key === 'Tab') {
            e.preventDefault();
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
            
            // Insert 2 spaces at cursor position
            const newValue = script.substring(0, start) + '  ' + script.substring(end);
            const sanitizedValue = sanitizeScript(newValue);
            setScript(sanitizedValue);
            
            // Set cursor position after the inserted spaces
            setTimeout(() => {
                e.target.selectionStart = e.target.selectionEnd = start + 2;
            }, 0);
            
            logUserAction('tab_indentation_used', {
                scriptType,
                cursorPosition: start,
                selectionLength: end - start,
                scriptLength: sanitizedValue.length
            });
        }
        
        // Escape key to close modal
        if (e.key === 'Escape') {
            logUserAction('escape_key_close', {
                scriptType,
                scriptLength: script.length,
                hasUnsavedChanges: script !== initialScript
            });
            
            onClose();
        }
        
        // Ctrl+S to save
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            
            logUserAction('keyboard_shortcut_save', {
                scriptType,
                scriptLength: script.length,
                hasValidationError: !!validationError
            });
            
            handleSave();
        }
    };
    
    // Handle input change with validation feedback and sanitization
    const handleInputChange = (e) => {
        const value = e.target.value;
        const previousLength = script.length;
        
        // Security: Sanitize script input while preserving functionality
        const sanitizedValue = sanitizeScript(value);
        setScript(sanitizedValue);
        
        // Log significant content changes (every 100 characters or major content changes)
        if (Math.abs(sanitizedValue.length - previousLength) >= 100 || 
            (sanitizedValue.length === 0 && previousLength > 0) ||
            (sanitizedValue.length > 0 && previousLength === 0)) {
            
            logUserAction('script_content_changed', {
                scriptType,
                previousLength,
                newLength: sanitizedValue.length,
                lengthDelta: sanitizedValue.length - previousLength,
                isEmpty: sanitizedValue.trim().length === 0
            });
        }
        
        // Clear validation error if fixed
        if (validationError) {
            let shouldClearError = false;
            
            if (validationError === ERROR_MESSAGES['SCRIPT_EMPTY'] && sanitizedValue.trim().length > 0) {
                shouldClearError = true;
            } else if (validationError === ERROR_MESSAGES['BASH_HEADER_MISSING'] && sanitizedValue.includes('#!/bin/bash')) {
                shouldClearError = true;
            } else if (validationError === ERROR_MESSAGES['CLOUD_CONFIG_HEADER_MISSING'] && sanitizedValue.includes('#cloud-config')) {
                shouldClearError = true;
            }
            
            if (shouldClearError) {
                setValidationError('');
                
                logInfo('Validation error auto-cleared', {
                    scriptType,
                    previousError: validationError,
                    newLength: sanitizedValue.length
                });
            }
        }
    };

    const handleClose = () => {
        logUserAction('modal_close_initiated', {
            scriptType,
            scriptLength: script.length,
            hasUnsavedChanges: script !== initialScript,
            hasValidationError: !!validationError,
            hasError: !!error
        });
        
        logInfo('Cloud-init modal closing', {
            scriptType,
            scriptLength: script.length,
            hadChanges: script !== initialScript
        });
        
        onClose();
    };
    
    if (!isOpen) return null;
    
    return (
        <>
            <style>
                {`
                    .custom-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: #555555 #2a2a2a;
                    }
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 6px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: #2a2a2a;
                        border-radius: 3px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: #555555;
                        border-radius: 3px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #666666;
                    }
                `}
            </style>
            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-2 backdrop-blur-sm">
                <div 
                    className="bg-[#131313] rounded-lg w-full max-w-2xl border border-[#222222] shadow-2xl"                 
                    style={{ 
                        transform: 'scale(0.9) translateY(0.5cm)', 
                        maxHeight: '90vh' 
                    }}
                >
                    {/* Header Section with Title and Close Button */}
                    <div className="p-4 border-b border-[#222222] flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="bg-[#161616] p-3 rounded-lg mr-3 border border-[#222222]">
                                <FaCode className="text-[#e0eacc]" size={18} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-white">Cloud-Init Configuration</h2>
                                <p className="text-gray-400 text-sm">
                                    Configure initialization scripts for your virtual machine
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 rounded-full hover:bg-[#1a1a1a] text-gray-400 hover:text-white transition-colors"
                            aria-label="Close"
                        >
                            <FaTimes />
                        </button>
                    </div>
                    
                    <div className="p-4 overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(90vh - 130px)' }}>
                        {/* Error message */}
                        {error && (
                            <div className="mb-4 p-3 bg-[#1a0b0b] border border-[#3a1818] text-[#ff8080] rounded-md text-sm">
                                <div className="flex items-start">
                                    <FaExclamationTriangle className="mt-0.5 mr-2 flex-shrink-0 text-[#ff8080]" />
                                    <div>{sanitizeText(error)}</div>
                                </div>
                            </div>
                        )}
                        
                        {/* Script Type Selector with Better Icons */}
                        <div className="mb-4">
                            <label className="block text-gray-300 mb-2 text-sm font-medium" id="script-type-label">Script Type</label>
                            <div className="flex space-x-3" role="radiogroup" aria-labelledby="script-type-label">
                                <button
                                    className={`px-4 py-2 rounded-md flex items-center ${
                                        scriptType === 'bash'
                                            ? 'bg-[#151515] text-[#e0eacc] border border-[#e0eacc]'
                                            : 'bg-[#151515] text-white border border-[#333333] hover:border-[#444444]'
                                    } transition-colors`}
                                    onClick={() => handleTypeChange('bash')}
                                    aria-pressed={scriptType === 'bash'}
                                    role="radio"
                                    aria-checked={scriptType === 'bash'}
                                >
                                    <FaTerminal className="mr-2" size={14} />
                                    Bash Script
                                </button>
                                <button
                                    className={`px-4 py-2 rounded-md flex items-center ${
                                        scriptType === 'cloud-config'
                                            ? 'bg-[#151515] text-[#e0eacc] border border-[#e0eacc]'
                                            : 'bg-[#151515] text-white border border-[#333333] hover:border-[#444444]'
                                    } transition-colors`}
                                    onClick={() => handleTypeChange('cloud-config')}
                                    aria-pressed={scriptType === 'cloud-config'}
                                    role="radio"
                                    aria-checked={scriptType === 'cloud-config'}
                                >
                                    <FaFileCode className="mr-2" size={14} />
                                    Cloud-Config
                                </button>
                            </div>
                        </div>
                        
                        {/* Security Warning with Improved Styling */}
                        <div className="mb-4 p-3 bg-[#2b1f10] border border-[#4d3b18] rounded-md">
                            <div className="flex items-start">
                                <FaExclamationTriangle className="text-[#f0b429] mr-3 mt-0.5 flex-shrink-0" size={16} />
                                <p className="text-[#f0b429] text-sm">
                                    <span className="font-semibold">Security Warning:</span> Cloud-init scripts run with 
                                    root privileges during VM initialization. These scripts can modify system files, 
                                    install software, and change system configurations.
                                </p>
                            </div>
                        </div>
                        
                        {/* Script Editor with Line Numbers and Better Styling */}
                        <div className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-gray-300 text-sm font-medium" htmlFor="script-content">
                                    Script Content
                                </label>
                                <button
                                    onClick={loadExample}
                                    className="flex items-center text-xs text-[#a5c77f] hover:text-[#e0eacc] bg-[#1a1a1a] px-3 py-1.5 rounded-md border border-[#333333] transition-colors"
                                    aria-label={`Load ${scriptType} example template`}
                                    type="button"
                                >
                                    <FaCloudDownloadAlt className="mr-1.5" size={12} />
                                    Load Example
                                </button>
                
                            </div>
                            <textarea
                                id="script-content"
                                ref={textareaRef}
                                value={script}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                className="w-full p-3 text-white h-52 font-mono text-sm ring-1 ring-[#cfd9bd] focus:ring-[#cfd9bd] focus:ring-2 focus:outline-none focus:border-none resize-none rounded-md custom-scrollbar"    
                                placeholder={
                                    scriptType === 'bash'
                                        ? '#!/bin/bash\n# Your bash commands here'
                                        : '#cloud-config\n# Your cloud-config directives here'
                                }
                                spellCheck="false"
                                aria-label={`${scriptType} script content`}
                                aria-invalid={!!validationError}
                                aria-describedby={validationError ? "script-error" : "script-size-info"}
                                wrap="off"
                                autoComplete="off"
                                autoCorrect="off"
                                data-script-type={scriptType}
                                maxLength={16384}
                                style={{
                                    outline: 'none',
                                    WebkitAppearance: 'none',
                                    boxShadow: 'none !important',
                                    backgroundColor: '#211f1f'
                                }}
                            />
                            {validationError && (
                                <p className="text-red-400 text-xs mt-2 flex items-start" id="script-error" role="alert">
                                    <FaExclamationTriangle className="mr-1 mt-0.5 flex-shrink-0" />
                                    {sanitizeText(validationError)}
                                </p>
                            )}
                            
                            <p className={`text-xs mt-2 flex items-center ${script.length > 16384 ? 'text-red-400' : 'text-gray-400'}`} id="script-size-info">
                                <FaInfoCircle className="mr-1.5" size={12} />
                                Maximum size: 16KB. Current: {script.length} characters
                                {script.length > 16384 ? ' (exceeds limit by ' + (script.length - 16384) + ' characters)' : ''}.
                            </p>
                        </div>
                        
                        {/* Documentation with Collapsible Section */}
                        <div className="mb-4">
                            <div className="bg-[#131313] rounded-md border border-[#222222] overflow-hidden">
                                <div className="p-3 border-b border-[#222222] flex items-center justify-between cursor-pointer"
                                     onClick={() => {
                                         const docsContent = document.getElementById('docs-content');
                                         const isHidden = docsContent.classList.contains('hidden');
                                         docsContent.classList.toggle('hidden');
                                         
                                         logUserAction('documentation_toggled', {
                                             scriptType,
                                             opened: isHidden
                                         });
                                     }}>
                                    <div className="flex items-center">
                                        <FaInfoCircle className="text-[#e0eacc] mr-2" size={14} />
                                        <h3 className="font-medium text-white text-sm">
                                            {scriptType === 'bash' ? 'Bash Script Guidelines' : 'Cloud-Config Guidelines'}
                                        </h3>
                                    </div>
                                    <FaChevronRight className="text-gray-400 transform transition-transform duration-200"
                                                    id="docs-chevron"
                                                    style={{ transform: 'rotate(90deg)' }} />
                                </div>
                                
                                <div className="p-3 text-xs text-gray-300 max-h-40 overflow-y-auto custom-scrollbar" id="docs-content">
                                    {scriptType === 'bash' ? (
                                        <ul className="space-y-1.5">
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Start with <code className="bg-[#222222] px-1.5 py-0.5 rounded text-gray-200">#!/bin/bash</code>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Commands will run with root privileges
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Use <code className="bg-[#222222] px-1.5 py-0.5 rounded text-gray-200">set -e</code> to exit on errors
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Include <code className="bg-[#222222] px-1.5 py-0.5 rounded text-gray-200">apt-get update</code> before installing packages
                                            </li>
                                        </ul>
                                    ) : (
                                        <ul className="space-y-1.5">
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Start with <code className="bg-[#222222] px-1.5 py-0.5 rounded text-gray-200">#cloud-config</code> on the first line
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Must be valid YAML format with correct indentation
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Common modules: <code className="bg-[#222222] px-1.5 py-0.5 rounded text-gray-200">users</code>, <code className="bg-[#222222] px-1.5 py-0.5 rounded text-gray-200">packages</code>, <code className="bg-[#222222] px-1.5 py-0.5 rounded text-gray-200">write_files</code>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-[#e0eacc] mr-1.5">•</span> 
                                                Indentation is significant in YAML - use spaces not tabs
                                            </li>
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Keyboard Shortcuts Info */}
                        <div className="mb-4 flex items-center text-xs text-gray-400">
                            <FaInfoCircle className="mr-2" size={12} />
                            <span>Keyboard shortcuts: <span className="bg-[#222222] px-1.5 py-0.5 rounded">Tab</span> for indentation, <span className="bg-[#222222] px-1.5 py-0.5 rounded">Ctrl+S</span> to save, <span className="bg-[#222222] px-1.5 py-0.5 rounded">Esc</span> to close</span>
                        </div>
                    </div>
                    
                    {/* Footer Section with Save Button Only */}
                    <div className="p-4 border-t border-[#222222] flex justify-end bg-[#151515]">
                        <button
                            onClick={handleSave}
                            className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                                validationError 
                                    ? 'bg-[#2a2a2a] cursor-not-allowed text-gray-500' 
                                    : 'bg-[#e0eacc] hover:bg-[#c8d9a9] text-black'
                            }`}
                            type="button"
                            disabled={!!validationError}
                        >
                            <FaSave className="mr-2" />
                            Save Script
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

// PropTypes for type checking
CloudInitModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    initialScript: PropTypes.string
};

// Default props
CloudInitModal.defaultProps = {
    initialScript: ''
};

export default React.memo(CloudInitModal);